import React, { useState } from "react";
import {
  Shield,
  Users,
  Award,
  TrendingUp,
  Target,
  Eye,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import SEO from "../components/SEO";

const About = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [emblaRef] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: false }),
  ]);

  const whyChooseUs = [
    {
      icon: Shield,
      title: "Expert IP Attorneys",
      description:
        "Expert legal team with decades of intellectual property experience. We protect your brand with precision and care.",
    },
    {
      icon: TrendingUp,
      title: "Transparent Pricing",
      description:
        "Clear, upfront pricing with no hidden fees. Comprehensive packages designed to fit your business needs.",
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description:
        "Full-service support from start to finish. Our team is available via phone, email, and live chat.",
    },
    {
      icon: Award,
      title: "Proven Track Record",
      description:
        "Over 10,000 successful registrations with a 98% approval rate. Trusted by businesses nationwide.",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "CEO, TechNova Solutions",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      text: "Trademark Facility made our brand registration process seamless. Their attention to detail and expert guidance gave us complete confidence in protecting our intellectual property. Highly recommend their services!",
    },
    {
      name: "Marcus Johnson",
      role: "Founder, Urban Threads Apparel",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      text: "As an Amazon seller, protecting my brand was crucial. The team handled everything from trademark registration to Brand Registry enrollment. Professional, fast, and affordable!",
    },
    {
      name: "David Chen",
      role: "CTO, Innovate Labs",
      image: "https://randomuser.me/api/portraits/men/67.jpg",
      text: "Their patent services exceeded our expectations. The attorneys are knowledgeable, responsive, and truly care about protecting your innovations. We've secured three patents with their help!",
    },
    {
      name: "Jennifer Rodriguez",
      role: "Author & Publisher",
      image: "https://randomuser.me/api/portraits/women/68.jpg",
      text: "I needed copyright protection for my books and Trademark Facility delivered exceptional service. Fast, affordable, and thorough. I now have peace of mind knowing my work is protected.",
    },
    {
      name: "Robert Thompson",
      role: "Restaurant Owner",
      image: "https://randomuser.me/api/portraits/men/85.jpg",
      text: "From trademark search to final registration, Trademark Facility provided outstanding service. Their detailed search report gave me confidence before investing in my brand. Worth every penny!",
    },
  ];

  const faqs = [
    {
      question: "What is a trademark and why do I need one?",
      answer:
        "A trademark is a unique symbol, word, phrase, or design that identifies and distinguishes your products or services from others. Registering your trademark provides exclusive legal rights to use the mark in commerce and prevents others from using similar marks that could confuse consumers. It's essential for protecting your brand identity and building brand recognition.",
    },
    {
      question: "How long does the trademark registration process take?",
      answer:
        "The typical trademark registration process takes 8-12 months from filing to registration. This includes the initial USPTO examination (3-4 months), publication for opposition (30 days), and final registration. With our expedited service, we can file your application within 24 hours of receiving your information.",
    },
    {
      question: "What are USPTO fees and are they included in your prices?",
      answer:
        'USPTO (United States Patent and Trademark Office) filing fees are government fees required to process your trademark application. These fees are separate from our service fees and are noted as "+ Federal Filing Fee" in our pricing. Current USPTO fees range from $250-$350 per class of goods/services.',
    },
    {
      question: "Can I register a trademark for multiple classes?",
      answer:
        "Yes, you can register a trademark for multiple classes of goods and services. Each class requires a separate filing fee. Our Multi-Class and Premium packages include coverage for multiple classes, providing comprehensive protection across different product or service categories.",
    },
    {
      question: "What happens if my trademark application is rejected?",
      answer:
        "If the USPTO issues an office action (objection), our attorneys will work with you to prepare a comprehensive response. Our Standard and higher packages include office action support. We have a 98% approval rate because we conduct thorough searches and prepare applications carefully to minimize rejections.",
    },
    {
      question: "Do I need a trademark search before filing?",
      answer:
        "Yes, a trademark search is highly recommended before filing. It identifies potential conflicts with existing marks and helps avoid costly rejections. All our packages include trademark searches - from basic searches in our Essentials package to comprehensive 50-state searches in our Professional package.",
    },
    {
      question: "How long does trademark protection last?",
      answer:
        "Once registered, a trademark lasts for 10 years and can be renewed indefinitely as long as you continue using the mark in commerce. You must file maintenance documents between the 5th and 6th year, and then renew every 10 years thereafter.",
    },
    {
      question: "What's the difference between TM and (R) symbols?",
      answer:
        "The TM symbol can be used with any mark to indicate trademark rights, even without registration. The (R) symbol can only be used after your trademark is officially registered with the USPTO. Using (R) before registration is illegal and can result in penalties.",
    },
    {
      question:
        "Can I trademark a business name that's already registered as an LLC?",
      answer:
        "State LLC registration and federal trademark registration are separate processes. Just because a business name is registered as an LLC in your state doesn't mean you have trademark rights nationwide. A federal trademark registration provides much broader protection and prevents others from using similar marks across the United States.",
    },
    {
      question: "What is your 100% Approval Guarantee?",
      answer:
        "Our 100% Approval Guarantee (included in Standard and higher packages) means that if your trademark application is rejected by the USPTO, we will work with you to overcome the rejection at no additional attorney fee. If we determine the mark cannot be registered, we will provide a full refund of our service fees (USPTO fees are non-refundable).",
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="about-page">
      <SEO
        title="About Us"
        description="Learn about Trademark Facility — a trusted team of IP attorneys and legal professionals protecting businesses across the United States since day one."
        path="/about"
        keywords="about federal trademark experts, IP attorneys, trademark lawyers USA"
      />
      {/* 1. About the Company - Hero + Story */}
      <section
        className="relative bg-[#07382f] overflow-hidden text-white py-12 md:py-20"
        data-testid="about-company-section"
      >
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8 md:mb-12">
            <div className="inline-flex items-center bg-lime/10 border border-lime/30 rounded-full px-4 py-2 mb-5 md:mb-6">
              <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
                ABOUT TRADEMARK FACILITY
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold mb-4 md:mb-6 tracking-tight leading-tight">
              Protecting Brands.
              <span className="text-lime"> Empowering Business.</span>
            </h1>
            <p className="text-sm sm:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Your trusted partner for intellectual property protection and
              business formation services across the United States.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="min-w-0">
              <img
                src="https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxsZWdhbCUyMG9mZmljZXxlbnwwfHx8fDE3NjQwMjUzMTZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Our team"
                className="w-full h-64 md:h-[420px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
            <div className="min-w-0">
              <div className="inline-block bg-lime/10 text-lime-dark text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-5 md:mb-6">
                OUR STORY
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-[#2d3132] mb-4 md:mb-6 leading-tight">
                Who We Are
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 mb-4 leading-relaxed">
                At Trademark Facility, we specialize in protecting your brand
                through fast, affordable, and reliable trademark registration
                services. We understand that your brand is more than just a name
                or logo - it's your business identity, your reputation, and your
                competitive advantage.
              </p>
              <p className="text-sm sm:text-lg text-gray-600 mb-4 leading-relaxed">
                Our team of experienced attorneys and IP specialists work
                tirelessly to simplify the complex legal process, allowing you
                to focus on what you do best: growing your business. With a
                proven track record of over 10,000 successful registrations and
                a 98% approval rate, we're committed to delivering exceptional
                results.
              </p>
              <p className="text-sm sm:text-lg text-gray-600 leading-relaxed">
                Whether you're a startup founder protecting your first brand, an
                established business expanding your IP portfolio, or an inventor
                securing patent protection, we're here to guide you every step
                of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Our Vision and Mission */}
      <section
        className="py-12 md:py-24 bg-gray-50"
        data-testid="vision-mission-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <div className="inline-block bg-lime/10 text-lime-dark text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-4">
              VISION & MISSION
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-[#2d3132] mb-4">
              What Drives Us Forward
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Our commitment to protecting intellectual property is rooted in a
              clear vision and purpose-driven mission.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10 border-2 border-transparent hover:border-lime transition-all duration-300 hover:-translate-y-1">
              <div className="bg-lime/10 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-5 md:mb-6">
                <Eye className="h-7 w-7 sm:h-8 sm:w-8 text-lime" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#2d3132] mb-4">
                Our Vision
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-lg">
                To become the most trusted name in intellectual property
                services in the United States, making brand protection
                accessible and affordable for every business - from solo
                entrepreneurs to Fortune 500 companies. We envision a
                marketplace where every brand is protected, every invention is
                secured, and every creative work is safeguarded.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-10 border-2 border-transparent hover:border-cyan-500 transition-all duration-300 hover:-translate-y-1">
              <div className="bg-cyan-100 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-5 md:mb-6">
                <Target className="h-7 w-7 sm:h-8 sm:w-8 text-cyan-600" />
              </div>
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#2d3132] mb-4">
                Our Mission
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm sm:text-lg">
                To simplify the complex world of intellectual property law by
                providing expert, affordable, and transparent services. We are
                committed to empowering businesses with the legal protection
                they need to grow confidently, compete fearlessly, and succeed
                in the global marketplace. Our mission is to deliver results,
                not just paperwork.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Why Choose Us */}
      <section
        className="py-12 md:py-24 bg-[#07382f] relative overflow-hidden"
        data-testid="why-choose-section"
      >
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-10 md:mb-16">
            <span className="inline-block bg-lime/10 border border-lime/30 text-lime text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-4">
              WHY CHOOSE US
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-white mb-5 md:mb-6">
              Why Businesses Trust Trademark Facility
            </h2>
            <p className="text-sm sm:text-base text-gray-400 max-w-3xl mx-auto">
              We're committed to providing exceptional service and delivering
              results that protect your business.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="group">
                  <div className="border border-white/10 rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:bg-white/5 hover:border-lime/30 hover:-translate-y-1 h-full">
                    <div className="bg-lime/15 w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:bg-lime! transition-colors duration-300">
                      <Icon className="h-6 w-6 text-lime group-hover:text-[#2d3132] transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Testimonials */}
      <section
        className="py-12 md:py-24 bg-gray-50"
        data-testid="testimonials-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-block bg-lime/10 text-lime-dark text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-4">
              TESTIMONIALS
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-[#2d3132] mb-4">
              What Our Clients Say
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Trusted by businesses nationwide for reliable service and results.
            </p>
          </div>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0 pl-4"
                >
                  <div className="bg-white rounded-2xl shadow-lg p-8 h-full transition-all duration-300 hover:shadow-xl mr-4">
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-5 w-5 text-yellow-400 fill-current"
                        />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 italic leading-relaxed">
                      "{t.text}"
                    </p>
                    <div className="flex items-center">
                      <img
                        src={t.image}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <div className="font-bold text-[#2d3132]">{t.name}</div>
                        <div className="text-gray-600 text-sm">{t.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5. FAQs */}
      <section className="py-12 md:py-24 bg-white" data-testid="faqs-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-block bg-lime/10 text-lime-dark text-xs sm:text-sm font-semibold px-4 py-2 rounded-full mb-4">
              FAQs
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-[#2d3132] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-sm sm:text-lg text-gray-600">
              Get answers to common questions about our services.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-slate-200 rounded-xl overflow-hidden"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() =>
                    setActiveFaq(activeFaq === index ? null : index)
                  }
                  className="w-full px-4 sm:px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-[#2d3132] pr-4 text-sm sm:text-base">
                    {faq.question}
                  </span>
                  {activeFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-lime flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {activeFaq === index && (
                  <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-slate-200">
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
